import React from 'react';
import PropTypes from 'prop-types';
import { get, omit, isFunction, isUndefined, isEmpty } from 'lodash';
import shortid from 'shortid';
import clxBind from 'classnames/bind';
import FieldLabel from '../FieldLabel';
import Input from '../Input';
import Row from '../Row';
import Col from '../Col';
import FormRow from '../FormRow';
import domOnlyProps from '../../utils/domOnlyProps';
import isFieldValid from '../../utils/isFieldValid';
import styles from './styles.module.scss';

const clx = clxBind.bind(styles);

export default class Field extends React.Component {
  static propTypes = {
    valueField: PropTypes.object,
    className: PropTypes.string,
    label: PropTypes.node,
    labelAlign: PropTypes.oneOf(['left', 'right']),
    labelRatio: PropTypes.number,
    labelRequired: PropTypes.bool,
    alignContent: PropTypes.bool,
    error: PropTypes.string,
    description: PropTypes.node,
    displayErrors: PropTypes.bool,
    layout: PropTypes.oneOf(['vertical', 'horizontal', 'inline']),
    renderAddonsStart: PropTypes.func,
    renderAddonsEnd: PropTypes.func,
    children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  };

  static defaultProps = {
    valueField: {},
    label: undefined,
    labelAlign: undefined,
    labelRatio: 25,
    labelRequired: false,
    alignContent: false,
    error: undefined,
    description: undefined,
    displayErrors: true,
    layout: undefined,
    renderAddonsStart: undefined,
    renderAddonsEnd: undefined,
    className: undefined,
    children: <Input />,
  };

  static contextTypes = {
    form: PropTypes.shape({
      layout: PropTypes.oneOf(['vertical', 'horizontal', 'inline']),
    }),
  };

  getInputProps() {
    const { valueField } = this.props;

    const props = {
      id: this.id,
      ...domOnlyProps(valueField),
      ...omit(this.props, [
        'valueField',
        'className',
        'label',
        'labelAlign',
        'labelRatio',
        'labelRequired',
        'alignContent',
        'error',
        'description',
        'displayErrors',
        'layout',
        'renderAddonsStart',
        'renderAddonsEnd',
        'children',
      ]),
    };

    if (!isFieldValid(valueField)) {
      props.mode = 'danger';
    }

    return props;
  }

  getLabelProps() {
    const { valueField, labelAlign, labelRequired } = this.props;
    const defaultLabelAlign = this.isHorizontal() ? 'right' : 'left';

    const props = {
      htmlFor: this.id,
      required: labelRequired,
      align: labelAlign || defaultLabelAlign,
    };

    if (!isFieldValid(valueField)) {
      props.mode = 'danger';
    }

    return props;
  }

  getLayout() {
    const { layout: propsLayout } = this.props;
    const contextLayout = get(this.context, 'form.layout');

    return propsLayout || contextLayout;
  }

  isVertical() {
    return this.getLayout() === 'vertical';
  }

  isHorizontal() {
    return this.getLayout() === 'horizontal';
  }

  isInline() {
    return this.getLayout() === 'inline';
  }

  id = shortid.generate();

  renderInput() {
    const { alignContent, children } = this.props;

    const containerClasses = clx({ 'align-content': alignContent });

    return (
      <div className={containerClasses}>
        {React.isValidElement(children)
          ? React.cloneElement(children, this.getInputProps())
          : children}
      </div>
    );
  }

  renderLabel() {
    const { label } = this.props;

    if (isUndefined(label)) {
      return null;
    }

    return (
      <div className={styles['label']}>
        <FieldLabel {...this.getLabelProps()}>{label}</FieldLabel>
      </div>
    );
  }

  renderHelp() {
    const { valueField, error, displayErrors, description } = this.props;

    const hasErrors = !isFieldValid(valueField) || error;

    const errorsEl = hasErrors && displayErrors && (
      <div className={styles['error']}>{error || valueField.error}</div>
    );

    const descriptionEl = !isEmpty(description) && (
      <div className={styles['description']}>{description}</div>
    );

    return errorsEl || descriptionEl;
  }

  renderValue() {
    const { label, renderAddonsStart, renderAddonsEnd, children } = this.props;

    const addonsStart = !!renderAddonsStart && renderAddonsStart();
    const addonsEnd = !!renderAddonsEnd && renderAddonsEnd();
    const help = this.renderHelp();

    if (isFunction(children)) {
      const inputProps = this.getInputProps();
      return children({ inputProps, addonsStart, addonsEnd, help });
    }

    const valueClasses = clx('value', {
      'no-label': isUndefined(label),
    });

    return (
      <div className={valueClasses}>
        {addonsStart}
        {this.renderInput()}
        {addonsEnd}
        {help}
      </div>
    );
  };

  render() {
    const { label, labelRatio, className } = this.props;
    const layout = this.getLayout();
    const isHorizontal = this.isHorizontal();
    const isInline = this.isInline();

    const value = this.renderValue();

    const fieldClasses = clx({
      inline: isInline,
    });

    if (isHorizontal) {
      return (
        <FormRow layout={layout} className={className}>
          <Row className={fieldClasses}>
            {!isUndefined(label) && <Col ratio={{ md: labelRatio }}>{this.renderLabel()}</Col>}
            <Col ratio={{ md: label ? 100 - labelRatio : 100 }}>{value}</Col>
          </Row>
        </FormRow>
      );
    }

    return (
      <FormRow vSpacing="sm" layout={layout} className={className}>
        <div className={fieldClasses}>
          {this.renderLabel()}
          {this.renderValue()}
        </div>
      </FormRow>
    );
  }
}
